import React from 'react';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { fg } from '@atlaskit/platform-feature-flags';

import { useExtensionListRelay as useExtensionList } from '../../web-client/hooks/useExtensionList';
import { GQLExtensionContextsFilterType } from '../../web-client/graphql/types';
import { Loader } from '../../web-runtime';

import { ForgeUIRenderer } from './main';
import { ExtensionNotInstalled } from './ExtensionNotInstalled';
import type { ForgeUIRendererProps, CloudOrWorkspaceId, ConfluenceCustomUIProps } from './types';
import type { GQLExtension } from '../../web-client/graphql/types';

type EmbeddedMacroRendererProps = Omit<ForgeUIRendererProps, 'extension'> &
	CloudOrWorkspaceId &
	ConfluenceCustomUIProps & {
		createGetContextToken?: (extension: GQLExtension) => () => Promise<string>;
		extensionId: string;
	};

const getLoadingComponent = (loadingComponent?: React.ReactNode) => {
	if (loadingComponent) {
		return <>{loadingComponent}</>;
	}
	return <Loader />;
};

const EmbeddedMacroRendererInner = (
	props: EmbeddedMacroRendererProps,
): React.JSX.Element | null => {
	const { extensionId, client, contextIds, loadingComponent, createGetContextToken, ...rest } =
		props;

	const { extensions, loading } = useExtensionList({
		contextIds,
		filter: [
			{
				type: GQLExtensionContextsFilterType.ExtensionType,
				value: ['xen:macro'],
			},
		],
	});

	const extension = extensions?.find((ext) => ext.id === extensionId);
	const queryCompleted = extensions !== undefined;
	const notInstalled =
		queryCompleted && !extension && fg('platform_validate_embedded_macro_installation');

	if (loading || !queryCompleted) {
		return getLoadingComponent(loadingComponent);
	}

	if (notInstalled) {
		return <ExtensionNotInstalled extensionId={extensionId} />;
	}

	if (!extension) {
		return null;
	}

	type ForgeUIRendererPropsWithExtension = React.ComponentProps<typeof ForgeUIRenderer>;

	let getContextToken = props.getContextToken;

	// Create getContextToken function with fresh extension object
	if (createGetContextToken) {
		getContextToken = createGetContextToken(extension);
	}

	const forgeRendererProps: ForgeUIRendererPropsWithExtension = {
		...rest,
		client,
		contextIds,
		extension,
		loadingComponent,
		getContextToken,
		isEmbeddedMacro: true,
	};
	return (
		<>
			{/* hidden span for testing */}
			<span hidden id="forge-ui-embedded-macro-renderer" />
			<ForgeUIRenderer {...forgeRendererProps} />
		</>
	);
};

// This wrapper is required to use the Relay environment provider for the useExtensionListRelay hook.
// Once Confluence has fully migrated to Relay, we can remove this wrapper and defer to their Relay environment
export const EmbeddedMacroRenderer = (props: EmbeddedMacroRendererProps): React.JSX.Element => {
	return (
		<AtlassianRelayEnvironmentProvider>
			<EmbeddedMacroRendererInner {...props} />
		</AtlassianRelayEnvironmentProvider>
	);
};
